import {pick} from '@benchling/js-utils';
import produce from 'immer';

import {
  DataParameterInfo,
  EquipmentParameterInfo,
  MaterialInputParameterInfo,
  MaterialOutputParameterInfo,
  ParameterInfo,
  ParameterStructuredTableMode,
  ProcedureParameterSchemaFieldName,
} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {assembleMinMaxString} from 'notebook/integrations/procedures/ProceduresUtil';
import toColumnWidth from 'notebook/structured-tables/columns/toColumnWidth';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {
  StructuredTableColumn,
  StructuredTableColumnGroups,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType, TypedValue} from 'notebook/tables/TableConstants';
import {REQUIRED_PROCEDURE_CONDITION_REPLICATE_STRUCTURED_TABLE_COLUMN} from 'procedures/tables/ProcedureConditionReplicateColumn';
import {conditionReplicateToTypedValue} from 'procedures/utils/ProcedureConditionReplicates';
import {assertNever} from 'util/Types';

// Keep this file in sync with benchling/notebook/structured_tables/constants/parameter_tables.py get_parameter_table_column_infos_by_mode.
// Only parameter confirmation, parameter measurement, and material input table shapes are defined so far.

type ParameterValueGetterFn<TParameterInfo> = (
  parameterInfo: TParameterInfo,
  tableMode: ParameterStructuredTableMode
) => string | TypedValue | null;
export interface ColumnInfo<TParameterInfo> {
  colName: string;
  colType: TableColumnType;
  colUuid: ParameterStructuredTableColumnUuid;
  colGroup?: StructuredTableColumnGroups;
  getFromPInfo?: ParameterValueGetterFn<TParameterInfo>;
  schemaFieldName?: ProcedureParameterSchemaFieldName;
  columnOverrides?: Partial<StructuredTableColumn>;
  // use this for non schematized fields to delegate to a column based on the data type of the parameter
  shouldDelegateColumnByDataType?: boolean;
}

/**
 * Fixed `provenanceUuid` values for parameter table columns (see `BaseStructuredTableColumn` in
 * `StructuredTableConstants`). All parameter tables share this enum’s UUID set—they are not unique per
 * table instance. Matches template/entry column id behavior: the same logical column keeps a stable id.
 *
 * Keep in sync with benchling/notebook/structured_tables/constants/parameter_tables.py#ParameterStructuredTableColumnUuid
 */
export enum ParameterStructuredTableColumnUuid {
  NAME_COLUMN_UUID = '00000000-c6b4-4022-b05b-05321df894da',
  MIN_MAX_COLUMN_UUID = '00000000-c407-4069-8507-8eb728541f77',
  DESCRIPTION_COLUMN_UUID = '00000000-2728-492b-ae57-9a10c2c46711',
  CONFIRMED_AT_COLUMN_UUID = '00000000-ca38-4295-9f92-8f6c69115596',
  COMMENTS_COLUMN_UUID = '00000000-f64c-421c-8ef7-20a6dd89860b',
  PLANNED_PRIMARY_VALUE_COLUMN_UUID = '00000000-3579-401f-93a4-73f38b5cc959',
  PRIMARY_VALUE_COLUMN_UUID = '00000000-34c2-4994-b9e5-8b9b9e1f329d',
  PLANNED_MATERIAL_AMOUNT_COLUMN_UUID = '00000000-34f8-41ad-974a-add53512baed',
  MATERIAL_AMOUNT_COLUMN_UUID = '00000000-bc7f-44ed-9ed5-d00bf3dd5d70',
  MATERIAL_CONTAINER_COLUMN_UUID = '00000000-7492-44fd-8cb0-93747cc52e40',
  EQUIPMENT_MODEL_COLUMN_UUID = '00000000-828e-44fe-a357-d12024ec6d84',
  CONDITION_REPLICATE_COLUMN_UUID = '00000000-6928-4287-bbe9-41b29b059013',
}

export function getDataParameterConfirmationTableColumns(): Array<ColumnInfo<DataParameterInfo>> {
  return [
    {
      colName: 'Parameter',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.NAME_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: DataParameterInfo) => pInfo.name,
    },
    {
      colName: 'Planned Setpoint',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.PLANNED_PRIMARY_VALUE_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: DataParameterInfo) => pInfo.value,
      schemaFieldName: ProcedureParameterSchemaFieldName.VALUE_FIELD_NAME,
    },
    {
      colName: 'Min - Max',
      colType: TableColumnType.PROCEDURE_PARAMETER_MIN_MAX,
      colUuid: ParameterStructuredTableColumnUuid.MIN_MAX_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: DataParameterInfo) => assembleMinMaxString(pInfo),
    },
    {
      colName: 'Description',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.DESCRIPTION_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: DataParameterInfo) => pInfo.description,
    },
    {
      colName: 'Confirm Setpoint',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.PRIMARY_VALUE_COLUMN_UUID,
      getFromPInfo: (pInfo: DataParameterInfo, tableMode: ParameterStructuredTableMode) => pInfo.value,
      schemaFieldName: ProcedureParameterSchemaFieldName.VALUE_FIELD_NAME,
    },
    {
      colName: 'Recorded At',
      colType: TableColumnType.CUSTOM,
      colUuid: ParameterStructuredTableColumnUuid.CONFIRMED_AT_COLUMN_UUID,
      columnOverrides: {
        customColumnConfig: {
          dataType: CustomColumnDataType.DATETIME,
        },
      },
    },
    {
      colName: 'Comments',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.COMMENTS_COLUMN_UUID,
    },
  ];
}

export function getDataParameterMeasurementTableColumns(): Array<ColumnInfo<DataParameterInfo>> {
  return produce(getDataParameterConfirmationTableColumns(), (draft) => {
    const valueColumn = draft.find(
      (columnInfo) =>
        columnInfo.schemaFieldName === ProcedureParameterSchemaFieldName.VALUE_FIELD_NAME &&
        columnInfo.colGroup !== StructuredTableColumnGroups.REFERENCE
    );
    if (!valueColumn) {
      throw new Error('Expected to find a value column for data parameters');
    }
    valueColumn.colName = 'Record Value';
    valueColumn.getFromPInfo = undefined;
  });
}

export function getMaterialInputParameterTableColumns(): Array<ColumnInfo<MaterialInputParameterInfo>> {
  return [
    {
      colName: 'Name',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.NAME_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: MaterialInputParameterInfo) => pInfo.name,
    },
    {
      colName: 'Planned Material',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.PLANNED_PRIMARY_VALUE_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: MaterialInputParameterInfo) => pInfo.plannedMaterial,
      schemaFieldName: ProcedureParameterSchemaFieldName.MATERIAL_FIELD_NAME,
    },
    {
      colName: 'Planned Amount',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.PLANNED_MATERIAL_AMOUNT_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: MaterialInputParameterInfo) => pInfo.plannedAmount,
      schemaFieldName: ProcedureParameterSchemaFieldName.AMOUNT_FIELD_NAME,
    },
    {
      colName: 'Description',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.DESCRIPTION_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: MaterialInputParameterInfo) => pInfo.description,
    },
    {
      colName: 'Material Used',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.PRIMARY_VALUE_COLUMN_UUID,
      getFromPInfo: (pInfo: MaterialInputParameterInfo) => pInfo.plannedMaterial,
      schemaFieldName: ProcedureParameterSchemaFieldName.MATERIAL_FIELD_NAME,
    },
    {
      colName: 'Amount Used',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.MATERIAL_AMOUNT_COLUMN_UUID,
      getFromPInfo: (pInfo: MaterialInputParameterInfo) => pInfo.plannedAmount,
      schemaFieldName: ProcedureParameterSchemaFieldName.AMOUNT_FIELD_NAME,
    },
    {
      colName: 'Container Used',
      colType: TableColumnType.CONTAINERS,
      colUuid: ParameterStructuredTableColumnUuid.MATERIAL_CONTAINER_COLUMN_UUID,
      // The value of the column_group doesn't apply in parameter table. It's a arbitrarily picked value forced by the toColumnDefinition.
      colGroup: StructuredTableColumnGroups.TARGET,
      schemaFieldName: ProcedureParameterSchemaFieldName.CONTAINER_FIELD_NAME,
    },
    {
      colName: 'Added At',
      colType: TableColumnType.CUSTOM,
      colUuid: ParameterStructuredTableColumnUuid.CONFIRMED_AT_COLUMN_UUID,
      columnOverrides: {
        customColumnConfig: {
          dataType: CustomColumnDataType.DATETIME,
        },
      },
    },
    {
      colName: 'Comments',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.COMMENTS_COLUMN_UUID,
    },
  ];
}

export function getMaterialOutputParameterTableColumns(): Array<ColumnInfo<MaterialOutputParameterInfo>> {
  return [
    {
      colName: 'Name',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.NAME_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: MaterialOutputParameterInfo) => pInfo.name,
    },
    {
      colName: 'Description',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.DESCRIPTION_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: MaterialOutputParameterInfo) => pInfo.description,
    },
    {
      colName: 'Material',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.PRIMARY_VALUE_COLUMN_UUID,
      schemaFieldName: ProcedureParameterSchemaFieldName.MATERIAL_FIELD_NAME,
    },
    {
      colName: 'Recorded At',
      colType: TableColumnType.CUSTOM,
      colUuid: ParameterStructuredTableColumnUuid.CONFIRMED_AT_COLUMN_UUID,
      columnOverrides: {
        customColumnConfig: {
          dataType: CustomColumnDataType.DATETIME,
        },
      },
    },
    {
      colName: 'Comments',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.COMMENTS_COLUMN_UUID,
    },
  ];
}

export function getEquipmentParameterTableColumns(): Array<ColumnInfo<EquipmentParameterInfo>> {
  return [
    {
      colName: 'Name',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.NAME_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: EquipmentParameterInfo) => pInfo.name,
    },
    {
      colName: 'Model(s)',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.EQUIPMENT_MODEL_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: EquipmentParameterInfo) => pInfo.models,
      shouldDelegateColumnByDataType: true,
    },
    {
      colName: 'Planned Equipment',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.PLANNED_PRIMARY_VALUE_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: EquipmentParameterInfo) => pInfo.plannedEquipment,
      schemaFieldName: ProcedureParameterSchemaFieldName.EQUIPMENT_FIELD_NAME,
    },
    {
      colName: 'Description',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.DESCRIPTION_COLUMN_UUID,
      colGroup: StructuredTableColumnGroups.REFERENCE,
      getFromPInfo: (pInfo: EquipmentParameterInfo) => pInfo.description,
    },
    {
      colName: 'Confirm Equipment',
      colType: TableColumnType.ROW_DELEGATE,
      colUuid: ParameterStructuredTableColumnUuid.PRIMARY_VALUE_COLUMN_UUID,
      getFromPInfo: (pInfo: EquipmentParameterInfo) => pInfo.plannedEquipment,
      schemaFieldName: ProcedureParameterSchemaFieldName.EQUIPMENT_FIELD_NAME,
    },
    {
      colName: 'Comments',
      colType: TableColumnType.STRING,
      colUuid: ParameterStructuredTableColumnUuid.COMMENTS_COLUMN_UUID,
    },
  ];
}

const PARAMETER_TABLE_CONDITION_REPLICATE_COLUMN_INFO: ColumnInfo<ParameterInfo> = {
  colName: REQUIRED_PROCEDURE_CONDITION_REPLICATE_STRUCTURED_TABLE_COLUMN.name,
  colType: REQUIRED_PROCEDURE_CONDITION_REPLICATE_STRUCTURED_TABLE_COLUMN.columnType,
  colUuid: ParameterStructuredTableColumnUuid.CONDITION_REPLICATE_COLUMN_UUID,
  colGroup: StructuredTableColumnGroups.REFERENCE,
  getFromPInfo: (pInfo: ParameterInfo) => {
    const {conditionReplicateInfo} = pInfo;
    if (!conditionReplicateInfo) {
      return null;
    }
    return conditionReplicateToTypedValue(
      pick(conditionReplicateInfo, 'procedureMethodExecutionInstanceId', 'conditionName', 'replicateNumber')
    );
  },
  columnOverrides: {width: toColumnWidth({columnType: TableColumnType.PROCEDURE_CONDITION_REPLICATES})},
};

export function getParameterStructuredTableColumnInfos(
  mode: ParameterStructuredTableMode,
  options: {isMultibatch?: boolean} = {}
): Array<ColumnInfo<ParameterInfo>> {
  const {isMultibatch} = options;
  let columnInfos: Array<ColumnInfo<ParameterInfo>> = [];
  switch (mode) {
    case ParameterStructuredTableMode.PARAMETER_CONFIRMATION:
      columnInfos = getDataParameterConfirmationTableColumns();
      break;
    case ParameterStructuredTableMode.PARAMETER_ACTUAL:
      columnInfos = getDataParameterMeasurementTableColumns();
      break;
    case ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION:
      columnInfos = getMaterialInputParameterTableColumns();
      break;
    case ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION:
      columnInfos = getMaterialOutputParameterTableColumns();
      break;
    case ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION:
      columnInfos = getEquipmentParameterTableColumns();
      break;
    default:
      assertNever(mode);
  }
  if (isMultibatch) {
    columnInfos.unshift(PARAMETER_TABLE_CONDITION_REPLICATE_COLUMN_INFO);
  }
  return columnInfos;
}
